import { Box, Group } from '@mantine/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { SlidePageCanvas } from './SlidePageCanvas';

export interface SectionSlideThumbnailVisual {
  /** 1-based PDF page number in the slide deck. */
  slide_page: number;
  sort_order: number;
}

export interface SectionSlideThumbnailsProps {
  doc: PDFDocumentProxy;
  /** The section's associated slides (sorted here by sort_order). */
  visuals: SectionSlideThumbnailVisual[];
  /** Native render width per thumbnail. Matches the editor picker (140px). */
  thumbnailWidth?: number;
}

const THUMBNAIL_WIDTH = 140;

/**
 * MED-1079 — read-only thumbnail row for a section's associated slides on the
 * prep read-side (coach prep + peer-leader prep).
 *
 * Mirrors the content editor's `SectionVisualsPicker` thumbnails (numbered
 * image previews) so the facilitator sees what each slide looks like instead of
 * a `📄 PAGE N` chip — but WITHOUT the editor's selection chrome (no toggle
 * button, no selection-position chip). The page render itself is the shared
 * `SlidePageCanvas` (high-DPI canvas), the same primitive used by the editor
 * picker and the live-session `SectionSlides`, so all three render authored
 * slides through one code path.
 *
 * The parent loads the deck PDF once via `useSlideDeckDoc` and passes the
 * `PDFDocumentProxy` down, so multiple instances (one per section) don't each
 * re-load the PDF.
 */
export function SectionSlideThumbnails({
  doc,
  visuals,
  thumbnailWidth = THUMBNAIL_WIDTH,
}: SectionSlideThumbnailsProps) {
  const sorted = visuals.slice().sort((a, b) => a.sort_order - b.sort_order);
  if (sorted.length === 0) return null;

  return (
    <Group gap="xs" align="flex-start" data-testid="section-slide-thumbnails">
      {sorted.map(v => (
        <Box
          key={v.slide_page}
          style={{
            width: thumbnailWidth,
            position: 'relative',
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 6,
            overflow: 'hidden',
            background: 'white',
            aspectRatio: '16 / 10',
          }}
          data-testid={`section-slide-thumb-${v.slide_page}`}
        >
          <SlidePageCanvas
            doc={doc}
            pageNum={v.slide_page}
            width={thumbnailWidth}
            ariaLabel={`Slide page ${v.slide_page}`}
            style={{ width: '100%' }}
          />
          {/* page-number chip (bottom-right) — matches the editor picker. */}
          <Box
            style={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              background: 'rgba(0,0,0,0.65)',
              color: 'white',
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            {v.slide_page}
          </Box>
        </Box>
      ))}
    </Group>
  );
}
